/**
 * Shared primitives styled to match the desktop app's QSS (buttons, inputs,
 * cards, badges). Hand-rolled rather than pulled from a component library so
 * the two frontends stay visually identical and the bundle stays small.
 */

import type { ButtonHTMLAttributes, InputHTMLAttributes, ReactNode, SelectHTMLAttributes } from "react";

export function cx(...parts: (string | false | null | undefined)[]): string {
  return parts.filter(Boolean).join(" ");
}

// -- buttons -----------------------------------------------------------------

type ButtonVariant = "default" | "primary" | "ghost" | "danger";

const BUTTON_BASE =
  "inline-flex items-center justify-center gap-2 rounded-lg px-3 py-2 text-sm font-medium " +
  "transition-colors select-none disabled:opacity-50 disabled:pointer-events-none " +
  "whitespace-nowrap cursor-pointer";

// Every bordered/filled variant gets the phosphor-blue glow border (btn-glow,
// see index.css); "ghost" stays glow-free since it's used for dense chrome
// (theme toggle, collapse arrow, per-row icon actions) where a halo around
// every icon would be visual noise rather than a call-to-action accent.
const BUTTON_VARIANTS: Record<ButtonVariant, string> = {
  default: "btn-glow bg-elevated border border-line text-fg hover:bg-hover active:bg-pressed",
  primary: "btn-glow bg-accent text-on-accent hover:bg-accent-hover active:bg-accent-pressed",
  ghost: "text-fg-secondary hover:bg-hover hover:text-fg",
  danger: "btn-glow btn-glow-danger bg-danger text-white hover:bg-danger-hover",
};

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
}

export function Button({ variant = "default", className, ...rest }: ButtonProps) {
  return <button className={cx(BUTTON_BASE, BUTTON_VARIANTS[variant], className)} {...rest} />;
}

// -- form controls -----------------------------------------------------------

const FIELD_BASE =
  "w-full rounded-lg border border-line bg-elevated px-3 py-2 text-fg " +
  "placeholder:text-fg-muted focus:border-accent focus:outline-none " +
  "disabled:opacity-60 disabled:cursor-not-allowed";

export function Input({ className, ...rest }: InputHTMLAttributes<HTMLInputElement>) {
  return <input className={cx(FIELD_BASE, className)} {...rest} />;
}

export function Select({ className, ...rest }: SelectHTMLAttributes<HTMLSelectElement>) {
  return <select className={cx(FIELD_BASE, "appearance-none pr-8", className)} {...rest} />;
}

export function Textarea({
  className,
  ...rest
}: InputHTMLAttributes<HTMLTextAreaElement> & { rows?: number }) {
  return <textarea className={cx(FIELD_BASE, "resize-y", className)} {...rest} />;
}

export function Label({ children, htmlFor }: { children: ReactNode; htmlFor?: string }) {
  return (
    <label htmlFor={htmlFor} className="mb-1 block text-xs font-medium text-fg-muted">
      {children}
    </label>
  );
}

export function Field({
  label,
  error,
  children,
  hint,
}: {
  label: string;
  error?: string | null;
  hint?: string;
  children: ReactNode;
}) {
  return (
    <div>
      <Label>{label}</Label>
      {children}
      {error ? (
        <p className="mt-1 text-xs text-danger">{error}</p>
      ) : hint ? (
        <p className="mt-1 text-xs text-fg-muted">{hint}</p>
      ) : null}
    </div>
  );
}

// -- containers --------------------------------------------------------------

export function Card({ children, className }: { children: ReactNode; className?: string }) {
  return <div className={cx("card p-4", className)}>{children}</div>;
}

export function SectionTitle({ children }: { children: ReactNode }) {
  return <h2 className="section-title mb-3">{children}</h2>;
}

export function PageHeader({
  title,
  subtitle,
  actions,
}: {
  title: string;
  subtitle?: string;
  actions?: ReactNode;
}) {
  return (
    <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
      <div>
        <h1 className="page-title">{title}</h1>
        {subtitle ? <p className="page-subtitle mt-0.5">{subtitle}</p> : null}
      </div>
      {actions ? <div className="flex flex-wrap items-center gap-2">{actions}</div> : null}
    </div>
  );
}

// -- feedback ----------------------------------------------------------------

export function Badge({
  children,
  tone = "neutral",
}: {
  children: ReactNode;
  tone?: "neutral" | "success" | "danger" | "warning" | "accent";
}) {
  const tones = {
    neutral: "bg-hover text-fg-secondary",
    success: "bg-success/15 text-success",
    danger: "bg-danger/15 text-danger",
    warning: "bg-warning/15 text-warning",
    accent: "bg-accent/15 text-accent",
  } as const;
  return (
    <span
      className={cx(
        "inline-flex items-center rounded-md px-2 py-0.5 text-xs font-medium",
        tones[tone],
      )}
    >
      {children}
    </span>
  );
}

export function Spinner({ className }: { className?: string }) {
  return (
    <span
      role="status"
      aria-label="Loading"
      className={cx(
        "inline-block size-4 animate-spin rounded-full border-2 border-current border-t-transparent",
        className,
      )}
    />
  );
}

export function ErrorNote({ children }: { children: ReactNode }) {
  return (
    <div
      role="alert"
      className="rounded-lg border border-danger/40 bg-danger/10 px-3 py-2 text-sm text-danger"
    >
      {children}
    </div>
  );
}

export function EmptyState({ title, hint }: { title: string; hint?: string }) {
  return (
    <div className="flex flex-col items-center justify-center gap-1 px-4 py-14 text-center">
      <p className="text-sm font-medium text-fg-secondary">{title}</p>
      {hint ? <p className="text-xs text-fg-muted">{hint}</p> : null}
    </div>
  );
}

/**
 * Tri-state checkbox. `indeterminate` is a DOM property with no HTML
 * attribute, so it has to be set through a ref callback.
 */
export function TriCheckbox({
  checked,
  indeterminate = false,
  onChange,
  disabled,
  label,
  className,
}: {
  checked: boolean;
  indeterminate?: boolean;
  onChange: (next: boolean) => void;
  disabled?: boolean;
  label: string;
  className?: string;
}) {
  return (
    <input
      type="checkbox"
      aria-label={label}
      checked={checked}
      disabled={disabled}
      ref={(node) => {
        if (node) node.indeterminate = indeterminate && !checked;
      }}
      onChange={(event) => onChange(event.target.checked)}
      onClick={(event) => event.stopPropagation()}
      className={cx(
        "size-4 shrink-0 cursor-pointer accent-accent disabled:cursor-not-allowed disabled:opacity-40",
        className,
      )}
    />
  );
}
